import { env } from "@config/env";
import { routePaths } from "@config/routes";
import { clearAuthSession } from "@features/auth/session/authSession";
import axios, { AxiosError, type AxiosRequestConfig, type Method } from "axios";
import { notify } from "../toaster/toast";

declare global {
  interface Window {
    __vccBusyActionContext?: {
      card: HTMLElement;
      action: "execute" | "export" | "button";
      startedAt: number;
    };
  }
}

export interface HttpErrorOptions {
  status?: number;
  statusText?: string;
  body?: string;
  validationErrors?: Array<{ propertyName?: string; errorMessage?: string }>;
}

export class HttpError extends Error {
  readonly status?: number;
  readonly statusText?: string;
  readonly body?: string;
  readonly validationErrors?: Array<{ propertyName?: string; errorMessage?: string }>;

  constructor(
    message: string,
    { status, statusText, body, validationErrors }: HttpErrorOptions = {},
  ) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.validationErrors = validationErrors;
  }
}

export type ApiRequestOptions = {
  method?: string;
  data?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  responseType?: AxiosRequestConfig["responseType"];
};

export const apiClient = axios.create({
  withCredentials: true,
  baseURL: env.apiBaseUrl || undefined,
  headers: {
    Accept: "application/json",
  },
});

let sessionExpiredRedirectPending = false;
let requestSequence = 0;

type TrackedAxiosRequestConfig = AxiosRequestConfig & {
  __vccBusyRequestId?: number;
};

apiClient.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const context = window.__vccBusyActionContext;
    if (context && Date.now() - context.startedAt < 1000) {
      const requestId = ++requestSequence;
      (config as TrackedAxiosRequestConfig).__vccBusyRequestId = requestId;
      window.dispatchEvent(
        new CustomEvent("vcc-api-request-start", {
          detail: { requestId, card: context.card, action: context.action },
        }),
      );
    }
  }
  return config;
});

function toHttpError(error: AxiosError): HttpError {
  const status = error.response?.status;
  const statusText = error.response?.statusText;
  const responseData = error.response?.data;

  let body = "";
  let parsed: Record<string, unknown> | null = null;

  if (typeof responseData === "string") {
    body = responseData;
    parsed = tryParseJson(responseData);
  } else if (responseData !== undefined && responseData !== null) {
    body = typeof responseData === "object" ? JSON.stringify(responseData) : String(responseData);
    parsed =
      typeof responseData === "object" && !Array.isArray(responseData)
        ? (responseData as Record<string, unknown>)
        : null;
  }

  const validationErrors = Array.isArray(parsed?.errors)
    ? (parsed.errors as Array<{ propertyName?: string; errorMessage?: string }>)
    : undefined;

  const message =
    (typeof parsed?.message === "string" && parsed.message) ||
    (typeof parsed?.title === "string" && parsed.title) ||
    body ||
    error.message ||
    (status ? `${status} ${statusText ?? ""}`.trim() : "Request failed");

  return new HttpError(message, { status, statusText, body, validationErrors });
}

apiClient.interceptors.response.use(
  (response) => {
    emitBusyRequestEnd(response.config, false);
    return response;
  },
  (error: AxiosError) => {
    emitBusyRequestEnd(error.config, true);
    if (error.code === AxiosError.ERR_CANCELED) {
      return Promise.reject(error);
    }

    const httpError = toHttpError(error);
    if (httpError.status != 200 && !shouldSuppressGlobalErrorToast(error)) {
      notify.error("Something went wrong");
    }
    if (httpError.status === 401 && typeof window !== "undefined") {
      const onLogin = window.location.pathname === routePaths.login;
      if (!onLogin && !sessionExpiredRedirectPending) {
        sessionExpiredRedirectPending = true;
        clearAuthSession();
        window.location.assign(routePaths.login);
      }
    }

    return Promise.reject(httpError);
  },
);

function emitBusyRequestEnd(config?: AxiosRequestConfig, failed?: boolean) {
  if (typeof window === "undefined") return;

  const requestId = (config as TrackedAxiosRequestConfig | undefined)?.__vccBusyRequestId;
  if (requestId === undefined) return;

  window.dispatchEvent(
    new CustomEvent("vcc-api-request-end", {
      detail: { requestId, failed: Boolean(failed) },
    }),
  );
}

function buildAxiosConfig(options?: ApiRequestOptions): AxiosRequestConfig {
  const method = (options?.method?.toUpperCase() ?? "GET") as Method;
  const isFormData = typeof FormData !== "undefined" && options?.data instanceof FormData;

  const config: AxiosRequestConfig = {
    method,
    signal: options?.signal,
    params: options?.params,
    headers: { ...options?.headers },
    responseType: options?.responseType,
  };

  if (
    method !== "GET" &&
    method !== "HEAD" &&
    options?.data !== undefined &&
    options?.data !== null
  ) {
    config.data = options.data;
  }
 debugger
  if (
    !isFormData &&
    options?.data !== undefined &&
    options?.data !== null &&
    typeof options.data === "object"
  ) {
    config.headers = {
      "Content-Type": "application/json",
      ...config.headers,
    };
  }

  return config;
}

export async function apiRequest<TResponse>(
  url: string,
  options?: ApiRequestOptions,
): Promise<TResponse> {
  const config = buildAxiosConfig(options);
  const response = await apiClient.request<TResponse>({
    ...config,
    url,
  });

  if (response.status === 204 || response.data === "" || response.data === undefined) {
    return null as TResponse;
  }

  return response.data;
}

export async function apiRequestBlob(url: string, options?: ApiRequestOptions): Promise<Blob> {
  const config = buildAxiosConfig({ ...options, responseType: "blob" });
  const response = await apiClient.request<Blob>({
    ...config,
    url,
  });
  return response.data;
}

export async function apiRequestBlobResponse(
  url: string,
  options?: ApiRequestOptions,
): Promise<{ data: Blob; fileName: string }> {
  const config = buildAxiosConfig({ ...options, responseType: "blob" });
  const response = await apiClient.request<Blob>({
    ...config,
    url,
  });

  return {
    data: response.data,
    fileName: response.headers["x-filename"],
  };
}

function shouldSuppressGlobalErrorToast(error: AxiosError): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.location.pathname !== routePaths.login) {
    return false;
  }

  const url = error.config?.url ?? "";
  return url.includes("/webapi/api/auth/");
}

function tryParseJson(text: string): Record<string, unknown> | null {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}
